const SCORE_IRRELEVANT_KEYS = new Set(['privacyConsent', 'accuracyConfirm']);

function areDeeplyEqual(
  left: unknown,
  right: unknown,
  comparedObjects: WeakMap<object, WeakSet<object>>,
): boolean {
  if (left == null && right == null) return true;
  if (left == null || right == null) return false;
  if (Object.is(left, right)) return true;

  if (left instanceof Date || right instanceof Date) {
    return left instanceof Date && right instanceof Date && Object.is(left.getTime(), right.getTime());
  }

  if (typeof left !== 'object' || typeof right !== 'object') return false;

  const comparedWithLeft = comparedObjects.get(left);
  // A revisited pair means a cycle or shared reference; report "unequal" so the caller treats
  // the change as relevant (conservative: over-invalidating is safe, missing a change is not).
  // Not reachable with the acyclic plain trees toObject() and Mongo produce.
  if (comparedWithLeft?.has(right)) return false;
  if (comparedWithLeft) comparedWithLeft.add(right);
  else comparedObjects.set(left, new WeakSet([right]));

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => areDeeplyEqual(value, right[index], comparedObjects));
  }

  if (Object.prototype.toString.call(left) !== '[object Object]') return false;
  if (Object.prototype.toString.call(right) !== '[object Object]') return false;
  if (Object.getOwnPropertySymbols(left).length || Object.getOwnPropertySymbols(right).length) return false;

  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const keys = new Set([...Object.keys(leftRecord), ...Object.keys(rightRecord)]);

  for (const key of keys) {
    if (!areDeeplyEqual(leftRecord[key], rightRecord[key], comparedObjects)) return false;
  }

  return true;
}

export function isScoreRelevantChange(
  oldEntity: Record<string, unknown>,
  update: Record<string, unknown>,
): boolean {
  try {
    // Compare only keys present in the update: partial entity saves (e.g. the language-sync
    // in client/startup.ts loads nothing but chosenLanguage) omit every unloaded field, and
    // treating those omissions as changes would wipe the score cache on every partial save.
    for (const key of Object.keys(update)) {
      if (SCORE_IRRELEVANT_KEYS.has(key)) continue;
      if (!areDeeplyEqual(oldEntity[key], update[key], new WeakMap())) return true;
    }

    return false;
  } catch {
    return true;
  }
}
